import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Users, BookOpen, Award, DollarSign, ArrowRight, Sparkles, CalendarCheck, Palette,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import RecentEnrollmentsCard from "@/components/admin/RecentEnrollmentsCard";
import EmailHealthBanner from "@/components/admin/EmailHealthBanner";

const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
};

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const { user } = useAuth();

    useEffect(() => {
        api.get("/admin/analytics").then((r) => setStats(r.data));
    }, []);

    return (
        <div className="relative max-w-7xl mx-auto px-6 py-12" data-testid="admin-dashboard">
            {/* Decorative grid backdrop */}
            <div
                aria-hidden
                className="absolute inset-x-0 top-0 h-72 -z-10 bg-grid-soft opacity-60"
            />

            <motion.div
                initial="hidden"
                animate="show"
                variants={stagger}
                className="space-y-10"
            >
                {/* Hero greeting */}
                <motion.div variants={fadeUp} className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="inline-flex items-center gap-1.5 chip chip-accent">
                            <Sparkles className="h-3 w-3" />
                            Administration
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl text-primary mt-3 tracking-tight">
                            Welcome back, {user?.name?.split(" ")[0] || "Admin"}
                        </h1>
                        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                            A calm overview of how Heritage is doing today. Take a breath, then
                            decide where to nudge things along.
                        </p>
                    </div>
                </motion.div>

                {/* Operational health alerts — only render when something
                    is actually wrong, so the dashboard stays calm on good days. */}
                <motion.div variants={fadeUp}>
                    <EmailHealthBanner />
                </motion.div>

                {/* KPI grid */}
                <motion.div
                    variants={stagger}
                    className="grid grid-cols-2 lg:grid-cols-4 gap-5"
                >
                    <Stat
                        label="Active users"
                        value={stats?.total_users ?? "—"}
                        delta="+12%"
                        icon={Users}
                        testid="stat-users"
                    />
                    <Stat
                        label="Programs"
                        value={stats?.total_programs ?? "—"}
                        delta="+3"
                        icon={BookOpen}
                        testid="stat-programs"
                    />
                    <Stat
                        label="Enrollments"
                        value={stats?.total_enrollments ?? "—"}
                        delta="+1"
                        icon={Award}
                        testid="stat-enrollments"
                    />
                    <Stat
                        label="Revenue"
                        value={stats ? `$${stats.revenue.toFixed(2)}` : "—"}
                        delta=" "
                        icon={DollarSign}
                        testid="stat-revenue"
                    />
                </motion.div>

                {/* Recently enrolled feed */}
                <motion.div variants={fadeUp}>
                    <RecentEnrollmentsCard />
                </motion.div>

                {/* Quick actions */}
                <div>
                    <h2 className="font-display text-xl text-primary mb-4">Jump back in</h2>
                    <motion.div
                        variants={stagger}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
                    >
                        <QuickAction
                            title="User management"
                            desc="Create, assign roles, activate or deactivate."
                            to="/admin/users"
                            testid="qa-users"
                            icon={Users}
                        />
                        <QuickAction
                            title="Program management"
                            desc="Set pricing, archive, trigger AI authoring."
                            to="/admin/programs"
                            testid="qa-programs"
                            icon={BookOpen}
                        />
                        <QuickAction
                            title="Attendance"
                            desc="See who's studying, export to Excel."
                            to="/admin/attendance"
                            testid="qa-attendance"
                            icon={CalendarCheck}
                        />
                        <QuickAction
                            title="Branding"
                            desc="Update name, tagline, college logo."
                            to="/admin/branding"
                            testid="qa-branding"
                            icon={Palette}
                        />
                    </motion.div>
                </div>
            </motion.div>
        </div>
    );
}

function Stat({ label, value, delta, icon: Icon, testid }) {
    return (
        <motion.div
            variants={fadeUp}
            whileHover={{ y: -3 }}
            transition={{ duration: 0.25 }}
            className="surface-card surface-card-lift p-6"
            data-testid={testid}
        >
            <div className="flex items-start justify-between">
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                    {label}
                </div>
                <div className="h-9 w-9 rounded-full bg-accent/15 text-accent-foreground/80 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-amber-700" />
                </div>
            </div>
            <div className="font-display text-4xl text-primary mt-3 tracking-tight">{value}</div>
            {delta?.trim() && (
                <div className="mt-3 inline-flex items-center gap-1 chip chip-success">
                    <ArrowRight className="h-3 w-3 -rotate-45" />
                    {delta} vs last month
                </div>
            )}
            {/* Decorative spark line */}
            <svg
                viewBox="0 0 120 32"
                className="spark-tray w-full mt-4 stroke-primary/30 fill-none"
                preserveAspectRatio="none"
                aria-hidden
            >
                <polyline
                    strokeWidth="1.5"
                    points="0,22 12,18 24,20 36,12 48,16 60,8 72,11 84,5 96,9 108,4 120,7"
                />
            </svg>
        </motion.div>
    );
}

function QuickAction({ title, desc, to, testid, icon: Icon }) {
    return (
        <motion.div
            variants={fadeUp}
            whileHover={{ y: -3 }}
            transition={{ duration: 0.25 }}
            className="surface-card surface-card-lift p-6 h-full flex flex-col"
            data-testid={testid}
        >
            <div className="h-10 w-10 rounded-lg bg-primary/8 text-primary flex items-center justify-center mb-3">
                <Icon className="h-5 w-5" />
            </div>
            <h3 className="font-display text-lg text-primary tracking-tight">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed flex-1">{desc}</p>
            <Button
                asChild
                size="sm"
                className="mt-4 bg-primary hover:bg-primary/90 self-start gap-1.5"
            >
                <Link to={to}>
                    Open
                    <ArrowRight className="h-3.5 w-3.5" />
                </Link>
            </Button>
        </motion.div>
    );
}
